import { useCallback, useEffect, useRef, useState } from 'react'
import { trackEvent } from '../lib/tracking'

type Cue = 'ambience' | 'music' | 'wake' | 'route' | 'bloom' | 'voice'
type OneShotCue = Exclude<Cue, 'ambience' | 'music'>

const preferenceKey = 'worlds-awake:sound'
const sources: Record<Cue, string> = {
  ambience: '/audio/AMB_SITE_SKY_LOOP_v01.mp3',
  music: '/audio/MUS_SITE_LUMINA_LOOP_v01.mp3',
  wake: '/audio/SFX_SITE_WORLD_HEART_WAKE_v01.mp3',
  route: '/audio/SFX_SITE_ROUTE_CONNECT_v01.mp3',
  bloom: '/audio/SFX_SITE_BLOOM_v01.mp3',
  voice: '/audio/VO_SITE_GUIDE_HERO_en-US_v01.mp3',
}

const targetVolume = { ambience: 0.22, music: 0.2 } as const

function fadeTo(item: HTMLAudioElement, volume: number, duration = 900) {
  const start = performance.now()
  const initial = item.volume
  const frame = (now: number) => {
    const progress = Math.min(1, (now - start) / duration)
    item.volume = initial + (volume - initial) * (1 - Math.cos(progress * Math.PI)) / 2
    if (progress < 1 && !item.paused) requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)
}

export function useAudio(enabled: boolean) {
  const [consented, setConsented] = useState(false)
  const consentedRef = useRef(false)
  const loops = useRef<Partial<Record<'ambience' | 'music', HTMLAudioElement>>>({})
  const oneShots = useRef<Partial<Record<OneShotCue, Set<HTMLAudioElement>>>>({})

  const stopAll = useCallback(() => {
    Object.values(loops.current).forEach((item) => item?.pause())
    Object.values(oneShots.current).forEach((items) => items?.forEach((item) => {
      item.pause()
      item.currentTime = 0
    }))
    oneShots.current = {}
  }, [])

  const startLoops = useCallback(async () => {
    if (!enabled || !consentedRef.current) return
    const ambience = loops.current.ambience ?? new Audio(sources.ambience)
    const music = loops.current.music ?? new Audio(sources.music)
    ambience.loop = true
    music.loop = true
    ambience.volume = 0
    music.volume = 0
    loops.current = { ambience, music }
    const results = await Promise.allSettled([ambience.play(), music.play()])
    if (!consentedRef.current) {
      stopAll()
      return
    }
    if (results[0].status === 'fulfilled') fadeTo(ambience, targetVolume.ambience)
    if (results[1].status === 'fulfilled') fadeTo(music, targetVolume.music)
  }, [enabled, stopAll])

  useEffect(() => {
    if (!enabled) return
    const stored = window.localStorage.getItem(preferenceKey) === 'on'
    if (stored) {
      consentedRef.current = true
      setConsented(true)
      void startLoops()
    }
    const handleVisibility = () => {
      if (document.hidden) stopAll()
      else if (consentedRef.current) void startLoops()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      stopAll()
      Object.values(loops.current).forEach((item) => {
        if (item) item.src = ''
      })
      loops.current = {}
    }
  }, [enabled, startLoops, stopAll])

  const setSound = useCallback(async (next: boolean) => {
    if (!enabled) return
    consentedRef.current = next
    setConsented(next)
    window.localStorage.setItem(preferenceKey, next ? 'on' : 'off')
    if (!next) {
      stopAll()
      return
    }
    trackEvent('audio_enabled')
    await startLoops()
  }, [enabled, startLoops, stopAll])

  const play = useCallback((cue: OneShotCue) => {
    if (!enabled || !consentedRef.current || document.hidden) return false
    oneShots.current[cue]?.forEach((active) => {
      active.pause()
      active.currentTime = 0
    })
    const item = new Audio(sources[cue])
    item.volume = cue === 'voice' ? 0.85 : 0.52
    const active = new Set<HTMLAudioElement>([item])
    oneShots.current[cue] = active
    const release = () => active.delete(item)
    item.addEventListener('ended', release, { once: true })
    void item.play().catch(release)
    return true
  }, [enabled])

  return { consented, setSound, play }
}
